import { useCallback, useEffect, useState } from 'react';

/**
 * Generic async state for a single admin-metrics fetch — shared by every
 * admin metrics section/page (F062 AdminMetricsPage, F133 AdminAcquisitionPage).
 * Extracted from AdminMetricsPage so new admin metric pages don't duplicate
 * the loading/error/ok state machine.
 */
export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'ok'; data: T };

export function useAdminMetric<T>(fetcher: () => Promise<T>): [AsyncState<T>, () => void] {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });

  const load = useCallback(() => {
    setState({ status: 'loading' });
    fetcher()
      .then((data) => setState({ status: 'ok', data }))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Error';
        setState({ status: 'error', error: message });
      });
  }, [fetcher]);

  useEffect(() => {
    load();
  }, [load]);

  return [state, load];
}
